import type { SbBlokData } from "@storyblok/js";
import type {
	FooterBlok,
	StoryblokAppLayout
} from "~/types/storyblok/storyblok";
import type { ExtractedFooterFields } from "~/types/components/footer";

/**
 * Build the combined footer payload from the Storyblok layout content.
 * Result lands in `appStore.footer` and is consumed by both the base
 * Footer (uses `blocks`/`combinedBlocks`/`copyright`) and the
 * template-specific Footers (read raw fields like `footer1..footer6`,
 * `title_footerN`, `footer_background_color` directly).
 */
export const extractFooterFields = (
	blok: StoryblokAppLayout | FooterBlok | null | undefined
): ExtractedFooterFields | null => {
	if (!blok) {
		return null;
	}

	const blocks: ExtractedFooterFields["blocks"] = {};
	const combinedBlocks: ExtractedFooterFields["combinedBlocks"] = {};

	for (let i = 1; i <= 6; i++) {
		const titleKey = `title_footer${i}` as keyof FooterBlok;
		const dataKey = `footer${i}` as keyof FooterBlok;
		const target = i <= 3 ? blocks : combinedBlocks;

		target[i] = {
			title: (blok[titleKey] as string) ?? "",
			key: dataKey,
			data: (blok[dataKey] as SbBlokData[]) ?? []
		};
	}

	const raw = blok as Partial<StoryblokAppLayout> & Partial<FooterBlok>;

	return {
		...raw,
		blocks,
		combinedBlocks,
		copyright: raw.footer_copyright as ExtractedFooterFields["copyright"],
		newsletter: raw.newsletter ?? [],
		items: raw.items ?? [],
		logo: raw.footer_logo
	};
};
